import { useTranslation } from "react-i18next";
import {
    ModuleName, PermissionAction, MODULE_DISPLAY_NAMES, ACTION_DISPLAY_NAMES,
} from "../../../types/permissions";
import { resolveRole, MOCK_ROLES } from "./roles-mock";

const MODULES: ModuleName[] = ["dashboard", "tickets", "customers", "users", "vendors", "products", "roles", "settings", "reports"];
const ACTIONS: PermissionAction[] = ["view", "create", "edit", "delete"];

// Splitter altinda acilan rol bilgi karti: ozet + modul/aksiyon yetki matrisi.
export function RoleInfoPane({ roleRef }: { roleRef: string }) {
    const { t } = useTranslation();
    const role = resolveRole(roleRef, MOCK_ROLES);

    if (!role) {
        return (
            <div className="flex items-center justify-center h-full w-full" style={{ color: "var(--subtitle-text-color)" }}>
                {t("roleNotFound")}
            </div>
        );
    }

    const initials = role.display_name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

    return (
        <div className="h-full w-full" style={{ overflowY: "auto", padding: 16, color: "var(--base-text-color)" }}>
            {/* Baslik */}
            <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
                <div className="flex items-center justify-center shrink-0" style={{ width: 48, height: 48, borderRadius: 10, background: "var(--accent-color, #6366F1)", color: "#fff", fontWeight: 600, fontSize: 16 }}>
                    {initials}
                </div>
                <div className="min-w-0">
                    <div className="font-semibold truncate" style={{ fontSize: 16 }}>{role.display_name}</div>
                    <div className="text-xs truncate" style={{ color: "var(--subtitle-text-color)" }}>
                        <span className="font-mono">{role.name}</span>
                        {role.is_active === false && <span style={{ color: "#EF4444", marginLeft: 8 }}>• {t("passive")}</span>}
                    </div>
                </div>
            </div>

            {/* Istatistik */}
            <div className="grid grid-cols-2 gap-3" style={{ marginBottom: 16 }}>
                <Stat label={t("users")} value={role.userCount} color="#6366F1" />
                <Stat label={t("modules")} value={MODULES.filter(m => role.permissions[m]?.view).length} color="#10B981" />
            </div>

            {role.description && (
                <div className="text-sm" style={{ color: "var(--subtitle-text-color)", marginBottom: 16 }}>{role.description}</div>
            )}

            {/* Yetki matrisi */}
            <div className="text-xs font-semibold uppercase" style={{ letterSpacing: "0.04em", color: "var(--subtitle-text-color)", marginBottom: 6 }}>
                {t("permissions")}
            </div>
            <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={{ color: "var(--subtitle-text-color)", fontSize: 12 }}>
                            <th style={{ ...thStyle, textAlign: "left" }}>{t("module")}</th>
                            {ACTIONS.map(a => <th key={a} style={thStyle}>{ACTION_DISPLAY_NAMES[a]}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {MODULES.map((m, i) => {
                            const perms = role.permissions[m];
                            return (
                                <tr key={m} style={{ borderTop: i === 0 ? "none" : "1px solid var(--border-color)" }}>
                                    <td style={{ ...tdStyle, textAlign: "left" }}>
                                        <span className="text-sm">{MODULE_DISPLAY_NAMES[m]}</span>
                                    </td>
                                    {ACTIONS.map(a => {
                                        // settings/reports/dashboard'da create/delete yok — uygulanamaz "—".
                                        const applicable = perms ? a in perms : false;
                                        const allowed = perms?.[a] === true;
                                        return (
                                            <td key={a} style={tdStyle}>
                                                {!applicable
                                                    ? <span style={{ color: "var(--subtitle-text-color)", opacity: 0.4 }}>—</span>
                                                    : allowed
                                                        ? <i className="dx-icon-check" style={{ color: "#10B981", fontSize: 15, fontWeight: 700 }} />
                                                        : <i className="dx-icon-close" style={{ color: "#E5535355", fontSize: 14 }} />}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const thStyle: React.CSSProperties = { padding: "8px 10px", fontWeight: 600, textAlign: "center" };
const tdStyle: React.CSSProperties = { padding: "8px 10px", textAlign: "center", verticalAlign: "middle" };

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
    return (
        <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
            <div className="text-xs" style={{ color: "var(--subtitle-text-color)" }}>{label}</div>
        </div>
    );
}
